import React, {Component} from "react"

import {SelectAsync} from "@irati/ui/src/FormItem"
import id from "@irati/utils/id"
import {injectIntl, FormattedMessage, InjectIntl} from "@irati/utils/lang"
import authentication from "@irati/auth"
import loadCompanyValues from "@irati/companyvalues/apollo/query/LoadCompanyValues"
import {CompanyValue} from "@irati/companyvalues/types"
import {Poll, Recognition} from "@irati/types"
import TrophyImg from "@irati/ui/src/Trophy"
import {StyledTrophyWrapper} from "./styled"
import getCompanyValueSearchText from "./CompanyValuesSearchText"

const PROPERTY = "companyValues"

const MIN_SEARCH_COUNT = 3

type CompanyValuesOptions = {
  data: CompanyValue
  label: string
  value: string
}

export interface Props extends InjectIntl {
  defaultOptions?: boolean
  item: Poll | Recognition
  label?: string
  labelKey?: string
  isMulti?: boolean
  onChange(item: Poll | Recognition, property: string, companyValues: string[]): void
  placeholder?: string
  stringlength?: string
  underLimit?: string
  valueKey?: string
}

interface State {
  searchString?: string
  companyValues: CompanyValue[]
}

class SelectCompanyValue extends Component<Props, State> {
  static defaultProps = {
    defaultOptions: true,
    id: "company-values-selector",
    label: "platform.fields.companyValue.label",
    labelKey: "tag",
    isMulti: true,
    placeholder: "platform.fields.companyValue.placeholder",
    stringlength: "platform.fields.companyValue.noneFound",
    underLimit: "platform.fields.companyValue.underCharLimit",
    valueKey: "uuid"
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      searchString: "",
      companyValues: []
    }
    this.load()
  }

  handleChange = (selectedCompanyValues: CompanyValue[]) => {
    const {item, onChange} = this.props
    const values = selectedCompanyValues.map((v) => v.uuid)
    onChange(item, PROPERTY, values)
  }

  handleInputChange = (searchString: string) => this.setState({searchString})

  load = () => {
    return this.getCompanyValues()
  }

  getCompanyValues = () => {
    const params = {
      limit: 9999,
      tenantId: authentication.userTenant().uuid
    }

    return loadCompanyValues(params).then(({results}) => {
      this.setState({
        companyValues: results
      })
      return results
    })
  }

  search = (option: CompanyValuesOptions) => {
    const {searchString} = this.state
    const {data: companyValue} = option
    if (searchString) {
      const t = companyValue.tag.toLowerCase()
      return t.includes(searchString.toLowerCase())
    }
    return true
  }

  getPromptText = () => {
    const {searchString} = this.state
    const {intl, hint, stringlength, underLimit} = this.props

    if (searchString && searchString.length >= MIN_SEARCH_COUNT) {
      return <FormattedMessage id={stringlength} />
    }

    return hint || <FormattedMessage id={underLimit} values={{count: intl.formatNumber(MIN_SEARCH_COUNT)}} />
  }

  render() {
    const idNamespace = id(PROPERTY, this.props)
    const tenant = authentication.userTenant()

    const {defaultOptions, formatMessage, item, label, isMulti, placeholder} = this.props

    let value = item.companyValues
    if (isMulti && item.companyValues?.length) {
      value = this.state.companyValues.filter((companyValue: CompanyValue) => item.companyValues?.includes(companyValue.uuid))
    }

    return (
      <SelectAsync
        {...this.props}
        autoComplete={false}
        defaultOptions={defaultOptions}
        id={idNamespace}
        filterOption={(option: CompanyValuesOptions) => this.search(option)}
        label={formatMessage({id: label}, {name: tenant.public_name})}
        loadOptions={this.load}
        isMulti={isMulti}
        name={idNamespace}
        onChange={this.handleChange}
        onInputChange={this.handleInputChange}
        components={{
          OptionIcon: ({icon}: CompanyValue) => {
            return icon ? (
              <StyledTrophyWrapper>
                <TrophyImg isSet size="30px" src={icon.href} />
              </StyledTrophyWrapper>
            ) : null
          },
          NoOptionsMessage: getCompanyValueSearchText(this.getPromptText()),
          ...(this.props.components || {})
        }}
        placeholder={placeholder}
        value={value}
      />
    )
  }
}

export default injectIntl(SelectCompanyValue)
